import { useMemo, useSyncExternalStore } from 'react';
import type {
  ScormUploadSession,
  StepScormUpload,
  StepScormUploadHandlers,
  StepScormUploadRetry,
  StepScormUploadStartOptions,
  StepScormUploadStatus,
  StepScormUploadSubject,
} from './types';

// Канал один, но маленький пакет не должен ждать за большим
const MAX_PARALLEL_UPLOADS = 2;
// Частей одной загрузки в пути одновременно
const PART_CONCURRENCY = 3;
// Паузы перед повторами части после неудачи; после последней — ошибка с докачкой
const PART_RETRY_DELAYS = [0, 3000, 5000, 10000, 20000, 60000];
// Со второй неудачи части подряд ссылки запрашиваются заново: на устаревшую или испорченную
// ссылку R2 отвечает 403 без CORS-заголовков, и браузер видит просто сетевую ошибку
const REFRESH_LINKS_AFTER_FAILURES = 2;
// Байты части не уходят дольше этого срока — соединение зависло, часть отправляется заново
const PART_STALL_TIMEOUT_MS = 5 * 60 * 1000;
// Сколько раз подтверждение досылает части, которых R2 не досчитался; дальше — ошибка с докачкой
const MAX_COMPLETE_ATTEMPTS = 2;
// Принятая загрузка видна ещё немного; дальше состояние шага приходит с сервера
const UPLOADED_KEEP_MS = 20000;
const UPLOADED_REFRESH_MS = 17000;

const PROCESSING_MESSAGE =
  'A SCORM package is still being processed. Try again when processing is finished';
const EXPIRED_MESSAGE = 'The upload has expired. Upload the file again.';
const CLOSED_MESSAGE =
  'This upload was cancelled or replaced in another tab or window. Choose the file again to upload it.';
const CONNECTION_MESSAGE = 'Upload error. Check your connection and resume the upload.';
const MISSING_PARTS_MESSAGE =
  'Some parts of the file did not reach the storage. Resume the upload.';

export const ACTIVE_UPLOAD_STATUSES: StepScormUploadStatus[] = [
  'queued',
  'preparing',
  'uploading',
  'paused',
  'completing',
];

export const isActiveScormUpload = (upload: StepScormUpload) =>
  ACTIVE_UPLOAD_STATUSES.includes(upload.status);

interface PartRequest {
  xhr: XMLHttpRequest;
  // Отправлено байт части
  loaded: number;
  stallTimer: ReturnType<typeof setTimeout> | null;
}

interface UploadEntry {
  state: StepScormUpload;
  file: File;
  handlers: StepScormUploadHandlers;
  // Первый заход продолжает незавершённую загрузку того же файла (resume), а не открывает новую
  resumeFirst: boolean;
  // Размер и число частей: известны, когда бэкенд открыл загрузку; 0 — ещё нет
  partSize: number;
  partCount: number;
  // Подписанные ссылки на части, которые ещё не приняты
  links: Map<number, string>;
  // Части, которые R2 принял
  accepted: Set<number>;
  // Части в очереди на отправку
  pending: number[];
  inFlight: Map<number, PartRequest>;
  // Части, ждущие повтора после неудачи
  delayed: Map<number, ReturnType<typeof setTimeout>>;
  // Неудачи подряд по части
  failures: Map<number, number>;
  // Бэкенд выдаёт свежие ссылки: новые части не уходят
  refreshing: boolean;
  completeAttempts: number;
  // Растёт при каждой остановке передачи (пауза, ошибка, отмена): ответы прежних запросов
  // после неё не применяются
  run: number;
}

interface ApiError {
  status?: number | string;
  data?: { message?: string; error?: { message?: string }; missingPartNumbers?: unknown };
  message?: string;
}

const asApiError = (error: unknown) => (error ?? {}) as ApiError;

const getErrorMessage = (error: unknown, fallback: string) => {
  const { data, message } = asApiError(error);
  return data?.message || data?.error?.message || message || fallback;
};

// 409 upload expired: срок приёма частей вышел или загрузки уже нет в R2
const isUploadExpired = (error: unknown) => {
  const { status, data } = asApiError(error);
  return status === 409 && /^upload expired/i.test(data?.message ?? '');
};

// Продолжать нечего: срок вышел, пакет шага сменили или убрали (409), шага или пакета нет (404)
const isUploadClosed = (error: unknown) => {
  const { status } = asApiError(error);
  return status === 404 || status === 409;
};

// 400 upload incomplete: части, которых R2 не досчитался при подтверждении
const getMissingPartNumbers = (error: unknown): number[] | null => {
  const { status, data } = asApiError(error);
  const missing = data?.missingPartNumbers;
  if (status !== 400 || !Array.isArray(missing)) return null;
  return missing.filter(
    (partNumber): partNumber is number => Number.isInteger(partNumber) && partNumber > 0,
  );
};

const getStartErrorMessage = (error: unknown) =>
  // Единственный 409 у старта: пакет шага ещё обрабатывается
  asApiError(error).status === 409
    ? PROCESSING_MESSAGE
    : getErrorMessage(error, 'Failed to prepare upload. Please try again.');

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

const preventUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  // Часть браузеров показывает предупреждение только при заданном returnValue
  // eslint-disable-next-line no-param-reassign
  event.returnValue = '';
};

/**
 * Загрузки SCORM-пакетов шагов живут здесь, а не в карточке или модалке: окно можно закрыть,
 * загрузка продолжится, а карточка при повторном открытии подхватит её состояние по stepId.
 * Бэкенд открывает multipart-загрузку в R2 и выдаёт подписанную ссылку на каждую часть; части
 * уходят прямо в R2 (XHR PUT, по PART_CONCURRENCY сразу), после последней бэкенд собирает файл
 * (complete) и ставит пакет в очередь обработки. Принятые части R2 хранит до срока загрузки,
 * поэтому обрыв сети и повтор продолжаются с недостающих частей (resume), а не с нуля.
 *
 * Докачка после перезагрузки страницы: файл из памяти пропал, принятые части остались. Карточка
 * узнаёт тот же файл по отпечатку незавершённой загрузки (scorm.upload из деталей урока), и
 * первый заход идёт через resume; если загрузку уже сменили или срок вышел — новая загрузка.
 *
 * Запись шага может смениться (отмена и новый файл), а передачу останавливают пауза и ошибка,
 * поэтому колбэки XHR и ответы бэкенда действуют, только пока запись числится за шагом
 * (current) и передача не останавливалась (run).
 */
class ScormUploadsStore {
  private entries = new Map<number, UploadEntry>();
  private listeners = new Set<() => void>();
  private snapshot: StepScormUpload[] = [];
  private guardsBound = false;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = () => this.snapshot;

  start = (
    stepId: number,
    file: File,
    subject: StepScormUploadSubject,
    handlers: StepScormUploadHandlers,
    options: StepScormUploadStartOptions = {},
  ) => {
    // Новая загрузка сама ставит заготовку на место прежней, отдельно удалять её не нужно
    this.drop(stepId);
    const resume = options.resume ?? false;
    this.entries.set(stepId, {
      state: {
        stepId,
        lessonTitle: subject.lessonTitle?.trim() || null,
        stepNumber: subject.stepNumber,
        fileName: file.name,
        fileSize: file.size,
        status: 'queued',
        progress: 0,
        error: null,
        retry: null,
        resumed: resume,
        ownsPackage: false,
      },
      file,
      handlers,
      resumeFirst: resume,
      partSize: 0,
      partCount: 0,
      links: new Map(),
      accepted: new Set(),
      pending: [],
      inFlight: new Map(),
      delayed: new Map(),
      failures: new Map(),
      refreshing: false,
      completeAttempts: 0,
      run: 0,
    });
    this.commit();
    this.pump();
  };

  /** Отмена: передача прерывается, заготовка шага убирается, у шага снова нет пакета */
  cancel = (stepId: number) => {
    const entry = this.drop(stepId);
    if (!entry) return;
    // Пока бэкенд открывал загрузку, заготовку уберёт продолжение begin()
    if (entry.state.ownsPackage) {
      void entry.handlers.removePackage().catch(() => undefined);
    }
    this.pump();
  };

  retry = (stepId: number) => {
    const entry = this.entries.get(stepId);
    if (!entry || entry.state.status !== 'error' || !entry.state.retry) return;
    entry.failures.clear();
    entry.completeAttempts = 0;
    if (entry.state.retry === 'resume' && entry.partSize > 0) {
      this.setState(entry, { status: 'uploading', error: null, retry: null });
      this.proceed(entry, true);
      return;
    }
    // Заново: новая загрузка заменит заготовку шага
    this.resetTransfer(entry);
    entry.resumeFirst = false;
    this.setState(entry, {
      status: 'queued',
      progress: 0,
      error: null,
      retry: null,
      resumed: false,
    });
    this.pump();
  };

  /** Запись убирается без действий на сервере: состояние шага уже известно из деталей урока */
  forget = (stepId: number) => {
    if (this.drop(stepId)) this.pump();
  };

  /** Запись шага, если за шагом всё ещё числится именно она */
  private current(expected: UploadEntry): UploadEntry | null {
    const entry = this.entries.get(expected.state.stepId);
    return entry === expected ? entry : null;
  }

  /** Ответ, полученный в этом заходе передачи, ещё применим */
  private isCurrentRun(entry: UploadEntry, run: number) {
    return this.current(entry) !== null && entry.run === run;
  }

  private drop(stepId: number): UploadEntry | null {
    const entry = this.entries.get(stepId);
    if (!entry) return null;
    this.stopTransfer(entry);
    this.entries.delete(stepId);
    this.commit();
    return entry;
  }

  private setState(entry: UploadEntry, patch: Partial<StepScormUpload>) {
    const current = this.current(entry);
    if (!current) return;
    current.state = { ...current.state, ...patch };
    this.commit();
  }

  private commit() {
    this.snapshot = Array.from(this.entries.values(), (entry) => entry.state);
    this.syncGuards();
    this.listeners.forEach((listener) => listener());
  }

  // Пока идёт загрузка: предупреждение при закрытии вкладки и слежение за сетью
  private syncGuards() {
    const hasActive = this.snapshot.some(isActiveScormUpload);
    if (hasActive && !this.guardsBound) {
      window.addEventListener('beforeunload', preventUnload);
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
      this.guardsBound = true;
    } else if (!hasActive && this.guardsBound) {
      window.removeEventListener('beforeunload', preventUnload);
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
      this.guardsBound = false;
    }
  }

  private pump() {
    let active = this.snapshot.filter(
      (upload) => upload.status !== 'queued' && isActiveScormUpload(upload),
    ).length;
    for (const entry of this.entries.values()) {
      if (active >= MAX_PARALLEL_UPLOADS) break;
      if (entry.state.status === 'queued') {
        active += 1;
        void this.begin(entry);
      }
    }
  }

  private async begin(entry: UploadEntry) {
    const started = this.current(entry);
    if (!started) return;
    this.setState(started, { status: 'preparing', error: null });
    let session: ScormUploadSession;
    try {
      session = await this.openUpload(started);
    } catch (error) {
      if (!this.current(started)) return;
      if (isOffline()) {
        this.setState(started, { status: 'paused' });
        return;
      }
      this.fail(started, getStartErrorMessage(error), 'restart');
      return;
    }
    const current = this.current(started);
    if (!current) {
      // Отменили, пока бэкенд открывал загрузку: заготовка на сервере уже есть
      void started.handlers.removePackage().catch(() => undefined);
      return;
    }
    this.applySession(current, session);
    this.setState(current, { status: 'uploading', ownsPackage: true });
    this.proceed(current, false);
  }

  /** Продолжить незавершённую загрузку того же файла, а если продолжать нечего — открыть новую */
  private async openUpload(entry: UploadEntry): Promise<ScormUploadSession> {
    const { handlers, file } = entry;
    if (entry.resumeFirst) {
      try {
        const session = await handlers.resume(file);
        this.markResumeTried(entry);
        return session;
      } catch (error) {
        // Сетевая ошибка: следующий заход снова попробует продолжить
        if (!isUploadClosed(error)) throw error;
        this.markResumeTried(entry);
        this.setState(entry, { resumed: false });
      }
    }
    return handlers.start(file);
  }

  private markResumeTried(entry: UploadEntry) {
    const current = this.current(entry);
    if (current) current.resumeFirst = false;
  }

  /** Ответ бэкенда о загрузке: размер частей, принятые части и ссылки на остальные */
  private applySession(entry: UploadEntry, session: ScormUploadSession) {
    const current = this.current(entry);
    if (!current) return;
    current.partSize = session.partSize;
    current.partCount = Math.max(1, Math.ceil(current.file.size / session.partSize));
    session.uploadedPartNumbers.forEach((partNumber) => current.accepted.add(partNumber));
    current.links = new Map(session.parts.map((part) => [part.partNumber, part.url]));
    current.pending = session.parts
      .map((part) => part.partNumber)
      .filter(
        (partNumber) =>
          !current.accepted.has(partNumber) &&
          !current.inFlight.has(partNumber) &&
          !current.delayed.has(partNumber),
      )
      .sort((a, b) => a - b);
    this.updateProgress(current);
  }

  /** Дальше по загрузке: все части приняты — подтверждение, иначе отправка остальных */
  private proceed(entry: UploadEntry, refreshLinks: boolean) {
    if (entry.accepted.size >= entry.partCount) {
      void this.finish(entry);
    } else if (refreshLinks) {
      void this.refresh(entry);
    } else {
      this.pumpParts(entry);
    }
  }

  /** Свежие ссылки на недостающие части и список принятых по данным R2, затем передача дальше */
  private async refresh(entry: UploadEntry) {
    const current = this.current(entry);
    if (!current || current.refreshing) return;
    current.refreshing = true;
    const { run } = current;
    let session: ScormUploadSession;
    try {
      session = await current.handlers.resume(current.file);
    } catch (error) {
      if (!this.isCurrentRun(current, run)) return;
      current.refreshing = false;
      this.failTransfer(current, error, 'Failed to continue the upload. Please try again.');
      return;
    }
    if (!this.isCurrentRun(current, run)) return;
    current.refreshing = false;
    this.applySession(current, session);
    this.proceed(current, false);
  }

  private pumpParts(entry: UploadEntry) {
    const current = this.current(entry);
    if (!current || current.state.status !== 'uploading' || current.refreshing) return;
    while (current.inFlight.size < PART_CONCURRENCY && current.pending.length > 0) {
      const partNumber = current.pending.shift() as number;
      if (current.accepted.has(partNumber) || current.inFlight.has(partNumber)) continue;
      const url = current.links.get(partNumber);
      if (!url) {
        current.pending.unshift(partNumber);
        void this.refresh(current);
        return;
      }
      this.sendPart(current, partNumber, url);
    }
    if (current.inFlight.size === 0 && current.pending.length === 0 && current.delayed.size === 0) {
      // Отправлять больше нечего: подтверждение проверит части и назовёт недостающие
      void this.finish(current);
    }
  }

  private sendPart(entry: UploadEntry, partNumber: number, url: string) {
    const start = (partNumber - 1) * entry.partSize;
    const end = Math.min(start + entry.partSize, entry.file.size);
    const xhr = new XMLHttpRequest();
    const request: PartRequest = { xhr, loaded: 0, stallTimer: null };
    const isLive = () => this.current(entry)?.inFlight.get(partNumber) === request;
    // Байты части не уходят дольше PART_STALL_TIMEOUT_MS — соединение зависло: часть уходит заново
    const watchStall = () => {
      if (request.stallTimer) clearTimeout(request.stallTimer);
      request.stallTimer = setTimeout(() => {
        request.stallTimer = null;
        if (!isLive()) return;
        xhr.abort();
        this.partFailed(entry, partNumber, request);
      }, PART_STALL_TIMEOUT_MS);
    };
    entry.inFlight.set(partNumber, request);
    xhr.upload.onprogress = (event) => {
      if (!isLive()) return;
      request.loaded = Math.min(event.loaded, end - start);
      watchStall();
      this.updateProgress(entry);
    };
    xhr.onload = () => {
      if (!isLive()) return;
      if (xhr.status >= 200 && xhr.status < 300) this.partAccepted(entry, partNumber, request);
      else this.partFailed(entry, partNumber, request);
    };
    xhr.onerror = () => {
      if (isLive()) this.partFailed(entry, partNumber, request);
    };
    // Тело — байты части без заголовков: ссылка подписана только на них
    xhr.open('PUT', url);
    xhr.send(entry.file.slice(start, end));
    watchStall();
  }

  private partAccepted(entry: UploadEntry, partNumber: number, request: PartRequest) {
    if (request.stallTimer) clearTimeout(request.stallTimer);
    entry.inFlight.delete(partNumber);
    entry.accepted.add(partNumber);
    entry.links.delete(partNumber);
    entry.failures.delete(partNumber);
    this.updateProgress(entry);
    this.pumpParts(entry);
  }

  private partFailed(entry: UploadEntry, partNumber: number, request: PartRequest) {
    if (request.stallTimer) clearTimeout(request.stallTimer);
    entry.inFlight.delete(partNumber);
    if (isOffline()) {
      this.pause(entry);
      return;
    }
    const failures = (entry.failures.get(partNumber) ?? 0) + 1;
    entry.failures.set(partNumber, failures);
    if (failures > PART_RETRY_DELAYS.length) {
      this.fail(entry, CONNECTION_MESSAGE, 'resume');
      return;
    }
    this.updateProgress(entry);
    const timer = setTimeout(
      () => {
        entry.delayed.delete(partNumber);
        const current = this.current(entry);
        if (!current) return;
        if (!current.pending.includes(partNumber)) current.pending.unshift(partNumber);
        if (failures >= REFRESH_LINKS_AFTER_FAILURES) void this.refresh(current);
        else this.pumpParts(current);
      },
      PART_RETRY_DELAYS[failures - 1],
    );
    entry.delayed.set(partNumber, timer);
  }

  private async finish(entry: UploadEntry) {
    const current = this.current(entry);
    if (!current) return;
    current.completeAttempts += 1;
    const { run } = current;
    this.setState(current, { status: 'completing', progress: 100 });
    try {
      await current.handlers.complete();
    } catch (error) {
      if (!this.isCurrentRun(current, run)) return;
      const missing = isOffline() ? null : getMissingPartNumbers(error);
      if (!missing) {
        this.failTransfer(current, error, 'Failed to finish the upload. Please try again.');
        return;
      }
      if (current.completeAttempts >= MAX_COMPLETE_ATTEMPTS) {
        this.fail(current, MISSING_PARTS_MESSAGE, 'resume');
        return;
      }
      // R2 не досчитался частей: они уходят заново по свежим ссылкам
      missing.forEach((partNumber) => current.accepted.delete(partNumber));
      this.setState(current, { status: 'uploading', progress: this.computeProgress(current) });
      void this.refresh(current);
      return;
    }
    if (!this.isCurrentRun(current, run)) return;
    this.setState(current, { status: 'uploaded', progress: 100, error: null });
    current.handlers.onUploaded?.();
    setTimeout(() => {
      if (this.current(current)?.state.status === 'uploaded') current.handlers.onUploaded?.();
    }, UPLOADED_REFRESH_MS);
    setTimeout(() => {
      const kept = this.current(current);
      if (kept && kept.state.status === 'uploaded') this.drop(kept.state.stepId);
    }, UPLOADED_KEEP_MS);
    this.pump();
  }

  /** Ошибка resume или complete: сеть, срок, чужой пакет или отказ бэкенда */
  private failTransfer(entry: UploadEntry, error: unknown, fallback: string) {
    if (isOffline()) {
      this.pause(entry);
      return;
    }
    if (isUploadExpired(error)) {
      this.fail(entry, EXPIRED_MESSAGE, 'restart');
      return;
    }
    if (isUploadClosed(error)) {
      // Пакет шага уже не эта загрузка: отмена его не трогает, новый файл выбирают в карточке
      this.setState(entry, { ownsPackage: false });
      this.fail(entry, CLOSED_MESSAGE, null);
      return;
    }
    this.fail(entry, getErrorMessage(error, fallback), 'resume');
  }

  private pause(entry: UploadEntry) {
    this.stopTransfer(entry);
    this.setState(entry, { status: 'paused', progress: this.computeProgress(entry) });
  }

  private fail(entry: UploadEntry, message: string, retry: StepScormUploadRetry) {
    this.stopTransfer(entry);
    this.setState(entry, {
      status: 'error',
      error: message,
      retry,
      progress: this.computeProgress(entry),
    });
    this.pump();
  }

  /** Обрывает идущие части и повторы; принятые части остаются за загрузкой */
  private stopTransfer(entry: UploadEntry) {
    const current = this.current(entry);
    if (!current) return;
    current.run += 1;
    current.refreshing = false;
    current.inFlight.forEach((request) => {
      if (request.stallTimer) clearTimeout(request.stallTimer);
      request.xhr.abort();
    });
    current.inFlight.clear();
    current.delayed.forEach((timer) => clearTimeout(timer));
    current.delayed.clear();
  }

  private resetTransfer(entry: UploadEntry) {
    this.stopTransfer(entry);
    const current = this.current(entry);
    if (!current) return;
    current.partSize = 0;
    current.partCount = 0;
    current.links.clear();
    current.accepted.clear();
    current.pending = [];
    current.failures.clear();
    current.completeAttempts = 0;
  }

  private partBytes(entry: UploadEntry, partNumber: number) {
    const start = (partNumber - 1) * entry.partSize;
    return Math.max(0, Math.min(entry.partSize, entry.file.size - start));
  }

  // До подтверждения не больше 99%: 100% — когда бэкенд собрал файл
  private computeProgress(entry: UploadEntry) {
    const { size } = entry.file;
    if (size <= 0 || entry.partSize <= 0) return 0;
    let sent = 0;
    entry.accepted.forEach((partNumber) => {
      sent += this.partBytes(entry, partNumber);
    });
    entry.inFlight.forEach((request) => {
      sent += request.loaded;
    });
    return Math.min(99, Math.floor((sent / size) * 100));
  }

  // Состояние меняется, только когда меняется процент: события XHR приходят часто
  private updateProgress(entry: UploadEntry) {
    const progress = this.computeProgress(entry);
    if (progress !== entry.state.progress) this.setState(entry, { progress });
  }

  private handleOnline = () => {
    for (const entry of this.entries.values()) {
      if (entry.state.status !== 'paused') continue;
      if (entry.partSize === 0) {
        void this.begin(entry);
      } else {
        this.setState(entry, { status: 'uploading' });
        this.proceed(entry, true);
      }
    }
  };

  private handleOffline = () => {
    for (const entry of this.entries.values()) {
      if (entry.state.status === 'uploading') this.pause(entry);
    }
  };
}

export const scormUploads = new ScormUploadsStore();

export const useScormUploads = () =>
  useSyncExternalStore(scormUploads.subscribe, scormUploads.getSnapshot);

// Строка сравнивается по значению: прогресс загрузок её не меняет
const getUploadStepIdsKey = () =>
  scormUploads
    .getSnapshot()
    .map((upload) => upload.stepId)
    .join(',');

/**
 * Шаги, у которых есть загрузка в менеджере. Перерисовывает только при появлении и уходе
 * загрузок, а не на каждом шаге прогресса — для страниц, которым прогресс не нужен
 */
export const useScormUploadStepIds = () => {
  const key = useSyncExternalStore(scormUploads.subscribe, getUploadStepIdsKey);
  return useMemo(() => (key ? key.split(',').map(Number) : []), [key]);
};

/** Загрузка пакета шага, если она идёт или закончилась ошибкой; stepId нет у несохранённого шага */
export const useStepScormUpload = (stepId?: number) => {
  const uploads = useScormUploads();
  return stepId ? (uploads.find((upload) => upload.stepId === stepId) ?? null) : null;
};

export const useStepScormUploadsFor = (stepIds: Array<number | undefined>) => {
  const uploads = useScormUploads();
  return uploads.filter((upload) => stepIds.includes(upload.stepId));
};
